import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
// Importamos la lista centralizada
import { cargos } from '../../../data/listasDesplegables';

const AdminContadores = () => {
    const navigate = useNavigate();
    const [contadores, setContadores] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    
    const [formData, setFormData] = useState({
        nombre: '', // O Razón Social
        apellido: '',
        dni: '',
        cuit: '',
        asociados: [] // Array dinámico de personas
    });

    const contadoresRef = collection(db, 'public/data/contadores');

    const fetchData = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(contadoresRef);
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Ordenar alfabéticamente por nombre
            data.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setContadores(data);
        } catch (error) { 
            console.error("Error al cargar contadores:", error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (item = null) => {
        if (item) {
            setFormData({
                nombre: item.nombre || '',
                apellido: item.apellido || '',
                dni: item.dni || '',
                cuit: item.cuit || '',
                asociados: item.asociados || []
            });
            setIsEditing(item.id);
        } else {
            setFormData({ 
                nombre: '', 
                apellido: '', 
                dni: '', 
                cuit: '', 
                asociados: [] 
            });
            setIsEditing(null);
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            // Limpieza de datos vacíos en asociados antes de guardar
            const payload = {
                ...formData,
                asociados: formData.asociados.filter(p => p.nombre || p.apellido || p.cargo)
            };
            
            if (isEditing) {
                await updateDoc(doc(db, 'public/data/contadores', isEditing), payload);
            } else {
                await addDoc(contadoresRef, payload);
            }
            
            setShowModal(false);
            fetchData();
        } catch (error) { 
            console.error(error);
            alert("Error al guardar contador"); 
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este contador? Esta acción no se puede deshacer.")) {
            try {
                await deleteDoc(doc(db, 'public/data/contadores', id));
                fetchData();
            } catch (error) {
                console.error(error);
                alert("Error al eliminar");
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Eliminado toUpperCase() para permitir ingreso natural de datos
        setFormData({ ...formData, [name]: value });
    };

    // --- Lógica para Asociados ---
    const addAsociado = () => {
        setFormData({
            ...formData,
            asociados: [...formData.asociados, { nombre: '', apellido: '', cargo: '', email: '', telefono: '' }]
        });
    };

    const removeAsociado = (index) => {
        const newAsociados = formData.asociados.filter((_, i) => i !== index);
        setFormData({ ...formData, asociados: newAsociados });
    };

    const handleAsociadoChange = (index, field, value) => {
        const newAsociados = [...formData.asociados];
        // Eliminado toUpperCase() para permitir ingreso natural de datos
        newAsociados[index][field] = value;
        setFormData({ ...formData, asociados: newAsociados });
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="max-w-6xl mx-auto">
                
                {/* HEADER */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div className="text-left w-full">
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Gestión de Contadores</h1>
                        <p className="text-gray-500 mt-1">Administra los estudios contables y sus contactos asociados.</p>
                    </div>
                    <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-bold shadow-sm hover:bg-gray-50 transition text-sm shrink-0">
                        Volver al Panel
                    </button>
                </div>

                {/* BOTÓN NUEVO */}
                <div className="flex justify-end mb-4">
                    <button onClick={() => handleOpenModal()} className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-bold shadow hover:bg-indigo-700 text-sm flex gap-2 items-center transition-all hover:scale-105">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                        Nuevo Contador
                    </button>
                </div>

                {/* TABLA */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">Razón Social / Nombre</th>
                                <th className="px-6 py-4">CUIT / DNI</th>
                                <th className="px-6 py-4">Contactos Asociados</th>
                                <th className="px-6 py-4 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {loading ? (
                                <tr><td colSpan="4" className="text-center py-8">Cargando...</td></tr>
                            ) : contadores.length === 0 ? (
                                <tr><td colSpan="4" className="text-center py-8 text-gray-400">No hay contadores registrados.</td></tr>
                            ) : (
                                contadores.map(item => (
                                    <tr key={item.id} className="hover:bg-indigo-50/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-900">{item.nombre} {item.apellido}</div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 font-mono">
                                            {item.cuit || item.dni || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.asociados && item.asociados.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {item.asociados.slice(0, 2).map((asoc, idx) => (
                                                        <span key={idx} className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-semibold border border-indigo-100">
                                                            {asoc.nombre} {asoc.apellido} ({asoc.cargo})
                                                        </span>
                                                    ))}
                                                    {item.asociados.length > 2 && (
                                                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs font-semibold">
                                                            +{item.asociados.length - 2} más
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 italic text-xs">Sin asociados</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleOpenModal(item)} className="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-indigo-50 transition" title="Editar">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
                                                </button>
                                                <button onClick={() => handleDelete(item.id)} className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition" title="Eliminar">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

            </div>

            {/* MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-fade-in flex flex-col border border-gray-100 max-h-[90vh]">
                        
                        {/* Modal Header */}
                        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center text-white shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                                </div>
                                <h3 className="font-bold text-lg">{isEditing ? 'Editar Contador' : 'Nuevo Contador'}</h3>
                            </div>
                            <button onClick={() => setShowModal(false)} className="hover:text-indigo-200 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        
                        <div className="overflow-y-auto p-6 flex-grow bg-white">
                            <form id="contadorForm" onSubmit={handleSave} className="space-y-6">
                                
                                {/* A - DATOS PRINCIPALES */}
                                <fieldset className="border border-indigo-100 rounded-xl p-5 bg-indigo-50/30 text-left">
                                    <legend className="text-xs font-bold text-indigo-600 uppercase tracking-wider px-2">Datos Principales</legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Nombre / Razón Social *</label>
                                            <input 
                                                type="text" name="nombre" 
                                                value={formData.nombre} onChange={handleChange} 
                                                className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                                                required placeholder="Ej: Estudio Contable Pérez"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Apellido (Opcional)</label>
                                            <input 
                                                type="text" name="apellido" 
                                                value={formData.apellido} onChange={handleChange} 
                                                className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">CUIT *</label>
                                            <input 
                                                type="number" name="cuit" 
                                                value={formData.cuit} onChange={handleChange} 
                                                className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                                                required placeholder="Sin guiones"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">DNI (Opcional)</label>
                                            <input 
                                                type="number" name="dni" 
                                                value={formData.dni} onChange={handleChange} 
                                                className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                                            />
                                        </div>
                                    </div>
                                </fieldset>

                                {/* B - PERSONAS ASOCIADAS */}
                                <fieldset className="border border-gray-200 rounded-xl p-5 relative text-left">
                                    <legend className="text-xs font-bold text-gray-500 uppercase tracking-wider px-2 bg-white">Personas Asociadas</legend>
                                    
                                    <div className="space-y-4 mt-2">
                                        {formData.asociados.map((asoc, index) => (
                                            <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-xl relative group hover:border-indigo-300 transition-colors">
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeAsociado(index)} 
                                                    className="absolute top-2 right-2 text-red-300 hover:text-red-600 font-bold p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Eliminar asociado"
                                                >
                                                    ✕
                                                </button>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <input 
                                                        type="text" placeholder="Nombre" 
                                                        value={asoc.nombre} 
                                                        onChange={(e) => handleAsociadoChange(index, 'nombre', e.target.value)}
                                                        className="input-small"
                                                    />
                                                    <input 
                                                        type="text" placeholder="Apellido" 
                                                        value={asoc.apellido} 
                                                        onChange={(e) => handleAsociadoChange(index, 'apellido', e.target.value)}
                                                        className="input-small"
                                                    />
                                                    <select 
                                                        value={asoc.cargo} 
                                                        onChange={(e) => handleAsociadoChange(index, 'cargo', e.target.value)}
                                                        className="input-small cursor-pointer"
                                                    >
                                                        <option value="">Seleccione Cargo...</option>
                                                        {cargos.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <input 
                                                        type="email" placeholder="Email" 
                                                        value={asoc.email} 
                                                        onChange={(e) => handleAsociadoChange(index, 'email', e.target.value)}
                                                        className="input-small md:col-span-2"
                                                    />
                                                    <input 
                                                        type="text" placeholder="Teléfono" 
                                                        value={asoc.telefono} 
                                                        onChange={(e) => handleAsociadoChange(index, 'telefono', e.target.value)}
                                                        className="input-small"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button 
                                        type="button" 
                                        onClick={addAsociado} 
                                        className="mt-4 w-full py-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 font-bold text-sm transition flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                                        Agregar Persona Asociada
                                    </button>
                                </fieldset>

                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                            <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 font-bold hover:bg-white hover:text-gray-800 transition text-sm">
                                Cancelar
                            </button>
                            <button type="submit" form="contadorForm" className="px-8 py-2.5 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-md transition transform hover:scale-[1.01] text-sm">
                                Guardar
                            </button>
                        </div>

                    </div>
                </div>
            )}

            <style>{`
                .input-small {
                    width: 100%;
                    border: 1px solid #e5e7eb;
                    padding: 0.5rem;
                    border-radius: 0.5rem;
                    font-size: 0.875rem;
                    outline: none;
                    transition: all 0.2s;
                }
                .input-small:focus {
                    border-color: #6366f1;
                    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
                }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
            `}</style>
        </div>
    );
};

export default AdminContadores;